import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const Frond = ({ length = 260, leaflets = 11, color = "#2a5636" }) => {
    const leaves = [];
    for (let i = 1; i <= leaflets; i++) {
        const t = i / leaflets;
        const y = -t * length;
        const size = (1 - t) * 34 + 7;
        leaves.push(
            <ellipse key={`l${i}`} cx={-size * 0.8} cy={y} rx={size} ry={size * 0.3}
                transform={`rotate(${-30 - t * 18} ${-size * 0.8} ${y})`} fill={color} />,
            <ellipse key={`r${i}`} cx={size * 0.8} cy={y} rx={size} ry={size * 0.3}
                transform={`rotate(${30 + t * 18} ${size * 0.8} ${y})`} fill={color} />
        );
    }
    return (
        <g>
            <path d={`M0 24 Q 12 ${-length * 0.5} 0 ${-length - 10}`} stroke={color} strokeWidth="5" fill="none" />
            {leaves}
        </g>
    );
};

const Rose = ({ x, y, s = 1 }) => (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
        {[0, 72, 144, 216, 288].map((a) => (
            <ellipse key={a} cx="0" cy="-10" rx="7.5" ry="12" transform={`rotate(${a})`} fill="#7e3222" />
        ))}
        <circle r="6.5" fill="#c84b31" />
        <circle r="2.6" fill="#e9b08a" />
    </g>
);

const Serpent = () => (
    <g>
        <path d="M100 400 C 55 345, 155 300, 112 248 C 76 204, 152 168, 120 118 C 98 82, 132 58, 118 26"
            stroke="#16301c" strokeWidth="15" fill="none" strokeLinecap="round" />
        <path d="M100 400 C 55 345, 155 300, 112 248 C 76 204, 152 168, 120 118 C 98 82, 132 58, 118 26"
            stroke="#2c5232" strokeWidth="5" fill="none" strokeLinecap="round" opacity="0.65" />
        <ellipse cx="118" cy="22" rx="10" ry="7" fill="#16301c" />
        <circle cx="112" cy="20" r="2.6" className="serpent-eye" />
        <circle cx="125" cy="21" r="2.6" className="serpent-eye" />
    </g>
);

export default function FoliageFrame() {
    const [whisper, setWhisper] = useState(false);

    return (
        <div className="foliage-frame" data-testid="foliage-frame">
            <AnimatePresence>
                {whisper && (
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.5 }}
                        className="whisper"
                        data-testid="serpent-whisper"
                    >
                        <p className="font-cormorant text-lg italic leading-relaxed text-[#e8d5a8]">
                            "Love less, I whispered, and you will hurt less. He has been loving less ever since."
                        </p>
                        <p className="font-cinzel mt-2 text-[0.6rem] tracking-[0.3em] text-[#9a8a68] uppercase">
                            — The Serpent, who remembers
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
            <svg className="foliage-piece foliage-left" viewBox="0 0 420 560" data-testid="foliage-left">
                <g transform="translate(70 560)"><Frond length={300} leaflets={12} /></g>
                <g transform="translate(160 560) rotate(14)"><Frond length={260} leaflets={11} color="#20492c" /></g>
                <g transform="translate(250 560) rotate(-10)"><Frond length={340} leaflets={13} /></g>
                <g transform="translate(330 560) rotate(22)"><Frond length={230} leaflets={10} color="#20492c" /></g>
                <g transform="translate(40 260)">
                    <g
                        className="serpent-zone"
                        data-testid="serpent-zone"
                        onMouseEnter={() => setWhisper(true)}
                        onMouseLeave={() => setWhisper(false)}
                        onClick={() => setWhisper((v) => !v)}
                    >
                        <Serpent />
                    </g>
                </g>
                <Rose x={310} y={470} s={1.4} />
                <Rose x={120} y={520} s={1.1} />
                <Rose x={230} y={430} s={0.9} />
            </svg>

            <svg className="foliage-piece foliage-right" viewBox="0 0 420 540" data-testid="foliage-right">
                <g transform="translate(350 540) scale(-1 1)"><Frond length={290} leaflets={12} /></g>
                <g transform="translate(260 540) rotate(12) scale(-1 1)"><Frond length={240} leaflets={10} color="#20492c" /></g>
                <g transform="translate(170 540) rotate(-12) scale(-1 1)"><Frond length={320} leaflets={12} /></g>
                <g transform="translate(80 540) rotate(20) scale(-1 1)"><Frond length={210} leaflets={9} color="#20492c" /></g>
                <Rose x={110} y={450} s={1.3} />
                <Rose x={300} y={500} s={1} />
                <Rose x={200} y={410} s={0.85} />
            </svg>

            <svg className="foliage-piece foliage-top" viewBox="0 0 1440 220" preserveAspectRatio="none" data-testid="foliage-top">
                {[80, 260, 460, 680, 900, 1120, 1320].map((x, i) => (
                    <g key={x} transform={`translate(${x} -10) rotate(${i % 2 === 0 ? 176 : 184})`}>
                        <Frond length={150 + (i % 3) * 30} leaflets={8} color={i % 2 === 0 ? "#0c2013" : "#10251630".slice(0, 7)} />
                    </g>
                ))}
            </svg>
        </div>
    );
}
